"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.estDisponibleA = estDisponibleA;
exports.instantDeReference = instantDeReference;
exports.eligiblePromotions = eligiblePromotions;
const errors_1 = require("../errors");
// Éligibilité des promotions (R9) : fonction pure, sans horloge ni fuseau codé
// en dur. Porte checkPromotionAvailability (promotion.ts:308-451) et
// checkPromotionConditions (l.494-573), dont la v1 lisait DateTime.local() et
// forçait 'Europe/Paris'.
//
// Écarts documentés (voir le rapport) :
// - bornes de dates incluses (la v1 comparait strictement) ;
// - fenêtre d'anniversaire REDÉFINIE (ruling) : la v1 calcule
//   [naissance + daysAfter − daysBefore, naissance + daysAfter], c'est-à-dire
//   quelques jours de l'année de NAISSANCE — une condition qu'aucune commande
//   ne peut satisfaire. Ici la fenêtre est lue au sens des noms de champs,
//   [anniversaire − daysBefore, anniversaire + daysAfter], l'anniversaire étant
//   projeté sur l'année en cours ;
// - la minute courante n'est plus décalée de +1 (v1
//   isAvailableFromAvailabilitySchedule) ;
// - `maxUses` et `uniqueByCustomer` se lisent dans `usages` (historique),
//   `repeatable` dans `occurrencesInOrder` (commande en cours) ;
// - `cumulative` s'évalue sur un ordre déterministe (priorité décroissante,
//   puis famille, puis identifiant) au lieu de l'ordre d'ajout de la v1, pour
//   que le résultat ne dépende pas de l'ordre des candidats.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;
const MILLISECONDS_PER_DAY = 86400000;
const WEEK_DAYS = {
    Mon: 'monday',
    Tue: 'tuesday',
    Wed: 'wednesday',
    Thu: 'thursday',
    Fri: 'friday',
    Sat: 'saturday',
    Sun: 'sunday',
};
function parseInstant(value, label) {
    if (!HAS_OFFSET.test(value)) {
        throw new errors_1.InvalidDateError(`${label} doit porter un fuseau explicite (Z ou ±hh:mm) — l'horloge machine n'est jamais consultée, reçu : ${value}`);
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
        throw new errors_1.InvalidDateError(`${label} n'est pas un horodatage ISO valide, reçu : ${value}`);
    }
    return parsed;
}
function localMoment(instant, timezone) {
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            weekday: 'short',
        }).formatToParts(new Date(instant));
    }
    catch {
        throw new errors_1.InvalidDateError(`eligiblePromotions : fuseau horaire inconnu « ${timezone} »`);
    }
    const read = (type) => parts.find((part) => part.type === type)?.value ?? '';
    const weekday = WEEK_DAYS[read('weekday')];
    if (weekday === undefined) {
        throw new errors_1.InvalidDateError(`eligiblePromotions : jour de semaine illisible pour le fuseau « ${timezone} »`);
    }
    return {
        year: Number(read('year')),
        month: Number(read('month')),
        day: Number(read('day')),
        hour: Number(read('hour')),
        minute: Number(read('minute')),
        weekday,
    };
}
function dayKey(moment) {
    return moment.year * 10000 + moment.month * 100 + moment.day;
}
function dateOnlyKey(value) {
    return Number(value.replaceAll('-', ''));
}
function isStarted(startDate, now, nowInstant) {
    if (startDate === undefined)
        return true;
    if (DATE_ONLY.test(startDate))
        return dayKey(now) >= dateOnlyKey(startDate);
    return nowInstant >= parseInstant(startDate, 'promotion.startDate');
}
function isNotEnded(endDate, now, nowInstant) {
    if (endDate === undefined)
        return true;
    if (DATE_ONLY.test(endDate))
        return dayKey(now) <= dateOnlyKey(endDate);
    return nowInstant <= parseInstant(endDate, 'promotion.endDate');
}
// Règle v1 : pas de grille, ou grille entièrement vide → toujours disponible ;
// jour sans plage → indisponible.
function isOpenNow(schedule, now) {
    if (schedule === undefined)
        return true;
    const allDays = Object.values(schedule);
    if (allDays.every((ranges) => (ranges ?? []).length === 0))
        return true;
    const minutes = now.hour * 60 + now.minute;
    const today = schedule[now.weekday] ?? [];
    return today.some((range) => {
        const start = range[0];
        const end = range[1];
        return start !== undefined && end !== undefined && minutes >= start && minutes <= end;
    });
}
// Disponibilité d'un menu ou d'un produit à un instant donné (le créneau de
// retrait choisi, ou maintenant), lue dans le fuseau du restaurant.
function estDisponibleA(schedule, instantIso, timezone) {
    if (schedule === null || schedule === undefined)
        return true;
    return isOpenNow(schedule, localMoment(parseInstant(instantIso, 'instantIso'), timezone));
}
// Instant auquel juger les horaires : l'heure de retrait quand le client l'a
// choisie (strictDueDate), sinon maintenant. Ne lève jamais : une dueDate
// absente, sans décalage ou illisible retombe sur maintenant.
function instantDeReference(order, maintenantIso) {
    const dueDate = order.dueDate;
    if (order.strictDueDate !== true || typeof dueDate !== 'string')
        return maintenantIso;
    if (!HAS_OFFSET.test(dueDate) || Number.isNaN(Date.parse(dueDate)))
        return maintenantIso;
    return dueDate;
}
function birthDayAndMonth(birthDate, timezone) {
    if (DATE_ONLY.test(birthDate)) {
        return { month: Number(birthDate.slice(5, 7)), day: Number(birthDate.slice(8, 10)) };
    }
    const moment = localMoment(parseInstant(birthDate, 'customer.birthDate'), timezone);
    return { month: moment.month, day: moment.day };
}
function isInBirthdayWindow(birthDate, condition, now, timezone) {
    const { daysBeforeBirthday, daysAfterBirthday } = condition;
    if (!Number.isInteger(daysBeforeBirthday) ||
        daysBeforeBirthday < 0 ||
        !Number.isInteger(daysAfterBirthday) ||
        daysAfterBirthday < 0) {
        throw new errors_1.InvalidQuantityError(`BIRTHDAY_WINDOW : les bornes en jours doivent être des entiers positifs, reçu : ${daysBeforeBirthday}/${daysAfterBirthday}`);
    }
    const birth = birthDayAndMonth(birthDate, timezone);
    const today = Date.UTC(now.year, now.month - 1, now.day);
    // Redéfinition assumée de la règle v1 (voir l'en-tête du fichier) : la
    // fenêtre encadre l'anniversaire, `daysBeforeBirthday` jours avant et
    // `daysAfterBirthday` jours après, bornes incluses, au jour près.
    // L'anniversaire est projeté sur l'année courante ; l'année précédente et la
    // suivante couvrent les fenêtres à cheval sur le 1er janvier.
    return [now.year - 1, now.year, now.year + 1].some((year) => {
        const anniversary = Date.UTC(year, birth.month - 1, birth.day);
        return (today >= anniversary - daysBeforeBirthday * MILLISECONDS_PER_DAY &&
            today <= anniversary + daysAfterBirthday * MILLISECONDS_PER_DAY);
    });
}
function satisfiesConditions(candidate, context, now) {
    for (const condition of candidate.eligibilityConditions) {
        if (condition.type === 'ORDER_TOTAL') {
            const satisfied = condition.operator === 'gte'
                ? context.orderSubtotal >= condition.value
                : context.orderSubtotal <= condition.value;
            if (!satisfied)
                return false;
        }
        if (condition.type === 'CUSTOMERS') {
            const customerId = context.customer?.id;
            if (condition.operator === 'IN' && (customerId === undefined || !condition.customerIds.includes(customerId))) {
                return false;
            }
            // NOT_IN : sans client identifié, la promotion reste ouverte (règle v1).
            if (condition.operator === 'NOT_IN' && customerId !== undefined && condition.customerIds.includes(customerId)) {
                return false;
            }
        }
        if (condition.type === 'BIRTHDAY_WINDOW') {
            const birthDate = context.customer?.birthDate;
            if (birthDate === undefined)
                return false;
            if (!isInBirthdayWindow(birthDate, condition, now, context.timezone))
                return false;
        }
    }
    return true;
}
function isAvailable(candidate, context, now, nowInstant) {
    if (candidate.disabled)
        return false;
    const shopsIds = candidate.shopsIds ?? [];
    if (shopsIds.length > 0 && !shopsIds.includes(context.shopId))
        return false;
    if (!isOpenNow(candidate.availabilitySchedule, now))
        return false;
    if (!isStarted(candidate.startDate, now, nowInstant))
        return false;
    if (!isNotEnded(candidate.endDate, now, nowInstant))
        return false;
    if (!candidate.orderModes.includes(context.mode))
        return false;
    // `repeatable` porte sur la commande en cours, `maxUses` sur l'historique :
    // deux compteurs distincts en v1 (samePromotionsInOrder vs promotionUsages).
    if (!candidate.repeatable && occurrencesOf(candidate.id, context) > 0)
        return false;
    const usage = context.usages.find((entry) => entry.promotionId === candidate.id);
    const count = usage?.count ?? 0;
    if (typeof candidate.maxUses === 'number' && count >= candidate.maxUses)
        return false;
    const customerId = context.customer?.id;
    if (candidate.uniqueByCustomer &&
        customerId !== undefined &&
        (usage?.customerIds ?? []).includes(customerId)) {
        return false;
    }
    return satisfiesConditions(candidate, context, now);
}
function occurrencesOf(promotionId, context) {
    return context.occurrencesInOrder?.[promotionId] ?? 0;
}
function familyRank(candidate) {
    switch (candidate.family) {
        case 'LEAST_EXPENSIVE':
            return 0;
        case 'CODE':
            return 1;
        case 'ACTIONS':
            return 2;
    }
}
function priorityOf(candidate) {
    return candidate.family === 'ACTIONS' ? candidate.priority : 0;
}
function eligiblePromotions(candidates, context) {
    const nowInstant = parseInstant(context.nowIso, 'context.nowIso');
    const now = localMoment(nowInstant, context.timezone);
    // Ordre déterministe : le résultat ne dépend pas de l'ordre des candidats,
    // seule la règle de cumul est séquentielle (v1).
    const ordered = [...candidates].sort((a, b) => priorityOf(b) - priorityOf(a) || familyRank(a) - familyRank(b) || a.id.localeCompare(b.id));
    const selected = [];
    for (const candidate of ordered) {
        if (!isAvailable(candidate, context, now, nowInstant))
            continue;
        // Cumul (v1) : une promotion non cumulable n'accepte aucune compagne, et
        // aucune promotion ne s'ajoute à une non cumulable déjà retenue. Une
        // promotion répétable déjà présente dans la commande échappe au blocage
        // (promotion.ts:390-399).
        const blocked = (!candidate.cumulative && selected.length > 0) || selected.some((other) => !other.cumulative);
        const repeats = candidate.repeatable && occurrencesOf(candidate.id, context) > 0;
        if (blocked && !repeats)
            continue;
        selected.push(candidate);
    }
    return selected;
}
